import React, { useState } from 'react';
import { Button, Card, Alert, Form } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';
import { Formik } from 'formik';
import * as Yup from 'yup';

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: Yup.string()
    .required('Password is required')
});

const LoginForm = () => {
  const [apiError, setApiError] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setApiError(null);
      setNeedsVerification(false);
      
      await login(values);
      navigate('/');
    } catch (error) {
      if (error.response?.data?.needsVerification) {
        setNeedsVerification(true);
        setUserEmail(error.response.data.email || values.email);
      } else {
        setApiError(error.response?.data?.message || 'Login failed. Please try again.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await authService.resendVerification({ email: userEmail });
      setApiError(null);
      setNeedsVerification(false);
      alert('Verification email sent. Please check your inbox.');
    } catch (error) {
      setApiError(error.response?.data?.message || 'Failed to resend verification email.');
    }
  };

  return (
    <Card className="p-4 shadow">
      <Card.Body>
        <h2 className="text-center mb-4">Login</h2>
        
        {apiError && <Alert variant="danger">{apiError}</Alert>}
        
        {needsVerification && (
          <Alert variant="warning">
            <Alert.Heading>Email Not Verified</Alert.Heading>
            <p>Your email address has not been verified. Please check your email for a verification link.</p>
            <hr />
            <Button 
              variant="outline-warning" 
              onClick={handleResendVerification}
              size="sm"
            >
              Resend Verification Email
            </Button>
          </Alert>
        )}
        
        <Formik
          initialValues={{ email: '', password: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.email && errors.email}
                  placeholder="your.email@example.com"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password *</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.password && errors.password}
                  placeholder="Enter your password"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="text-end mb-3">
                <Link to="/forgot-password" className="text-muted small">
                  Forgot password?
                </Link>
              </div>

              <Button 
                type="submit" 
                variant="primary" 
                className="w-100" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Logging in...' : 'Login'}
              </Button>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default LoginForm;
